import { apiClient, apiClientPaginated } from '@/lib/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

export interface Ingredient {
  id: string;
  location_id: string;
  name: string;
  category: string | null;
  unit: string;
  price: number;
  cost: number;
  stock_quantity: number;
  low_stock_threshold: number;
  is_active: boolean;
}

export type StockAdjustmentType = 'PURCHASE' | 'ADJUSTMENT' | 'DAMAGE' | 'OPENING_STOCK';

interface PaginationMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

interface IngredientListResponse {
  data: Ingredient[];
  meta: PaginationMeta;
}

// Tenant-wide KPI aggregate (GitHub issue #284). Computed server-side across
// every active ingredient — never the capped 500-row slice the table fetches —
// so the Total / Low Stock / Out of Stock cards count every ingredient and
// never undercount a critically-low one sitting past the 500th row.
export interface IngredientStats {
  total_ingredients: number;
  low_stock: number;
  out_of_stock: number;
}

interface AdjustStockRequest {
  type: StockAdjustmentType;
  quantity: number;
  unit_cost?: number;
  notes?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return '?' + entries.map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`).join('&');
}

// ─── Service ────────────────────────────────────────────────────────────────

export const ingredientService = {
  // GET /api/v1/ingredients
  async listIngredients(params: {
    location_id?: string;
    category?: string;
    is_active?: string;
    page?: number;
    limit?: number;
  } = {}): Promise<IngredientListResponse> {
    return apiClientPaginated<Ingredient[]>(`/ingredients${buildQuery({
      location_id: params.location_id,
      category: params.category,
      is_active: params.is_active,
      page: params.page,
      limit: params.limit ?? 200,
    })}`);
  },

  // GET /api/v1/ingredients/stats — tenant-wide KPI aggregate (GitHub issue #284)
  async getStats(params: {
    location_id?: string;
    category?: string;
    is_active?: string;
  } = {}): Promise<IngredientStats> {
    return apiClient<IngredientStats>(`/ingredients/stats${buildQuery({
      location_id: params.location_id,
      category: params.category,
      is_active: params.is_active,
    })}`);
  },

  // POST /api/v1/ingredients/:id/adjust-stock — restock / adjustment (MANAGER+)
  async adjustStock(id: string, dto: AdjustStockRequest): Promise<Ingredient> {
    return apiClient<Ingredient>(`/ingredients/${id}/adjust-stock`, {
      method: 'POST',
      body: dto,
    });
  },
};
